import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';

/**
 * ADMINISTRATION — L'ENVOI DES COURRIELS (lot 3.S-a)
 * ===================================================
 * Spec : doc/specs_techniques/courriel_smtp.md §3, §3 bis, §4 bis, §5 bis (SM4)
 * Maquette : doc/maquettes/admin_maquette.html — ligne « Envoi des courriels » de la
 * carte département, modale `#courrielModal`, cartes Configuration « réglage par
 * défaut » et « Redirection — serveur de test ».
 *
 * ⚠️ LE MOT DE PASSE NE REVIENT JAMAIS DU SERVEUR. L'écran ne connaît que
 * « défini le … par … » ; le champ de saisie n'apparaît qu'après « Remplacer », et
 * il est vidé dès l'enregistrement.
 *
 * ⚠️ L'ENVOI NE S'ACTIVE QU'APRÈS UN TEST RÉUSSI sur les réglages en place. Le
 * serveur le refuse de toute façon ; l'écran grise l'interrupteur pour ne pas
 * proposer un geste voué à l'échec.
 */

const FOURNISSEURS = [
    { cle: 'partage', libelle: 'Partage (université)' },
    { cle: 'relais', libelle: 'Relais de la DSI (sans mot de passe)' },
    { cle: 'gmail', libelle: 'Gmail — tests seulement' },
    { cle: 'autre', libelle: 'Autre' }
];

const dateHeure = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
        + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};
const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '');

/** L'étiquette d'état de la carte — maquette : mailTag(). */
function EtiquetteEtat({ etat }) {
    if (!etat || etat.etat === 'non') return <span className="tag">Non configuré</span>;
    if (etat.etat === 'echec') {
        return <span className="tag tag-ko"><i className="fa-solid fa-triangle-exclamation" /> {etat.echecs} échec{etat.echecs > 1 ? 's' : ''}</span>;
    }
    if (etat.etat === 'actif') return <span className="tag tag-ok">Activé · testé le {dateCourte(etat.teste_le)}</span>;
    return <span className="tag tag-warn">Désactivé</span>;
}

/** La ligne « Envoi des courriels » d'une carte département. */
export function CourrielLigneCarte({ etat, environnement, onRegler }) {
    const redirige = environnement?.redirection_imposee && etat && etat.etat !== 'non';
    return (
        <div className="dc-mail">
            <i className="fa-solid fa-envelope" />
            <span className="dc-mail-libelle">Envoi des courriels</span>
            <EtiquetteEtat etat={etat} />
            {redirige && (
                <span className="tag tag-warn"
                      title={`Serveur de test : tous les envois partent à ${environnement.redirection_adresse || '(adresse non réglée)'}`}>
                    <i className="fa-solid fa-flask" /> redirigé
                </span>
            )}
            <span className="grow" />
            <button className="a-btn a-btn-secondary a-btn-sm" onClick={onRegler}>
                <i className="fa-solid fa-gear" /> Régler
            </button>
        </div>
    );
}

/** Les bandeaux d'environnement : poste de développement (Mailpit), serveur de test (redirection). */
function BandeauxEnvironnement({ environnement, portee }) {
    if (!environnement) return null;
    return (
        <>
            {environnement.serveur_local && (
                <div className="mail-band info">
                    <i className="fa-solid fa-laptop-code" />
                    <div>
                        <b>Poste de développement</b> : tous les messages partent dans Mailpit
                        ({environnement.serveur_local}), rien ne sort. Page des messages : <b>http://localhost:8025</b>.
                    </div>
                </div>
            )}
            {environnement.redirection_imposee && (
                <div className="mail-band warn">
                    <i className="fa-solid fa-flask" />
                    <div>
                        {environnement.redirection_adresse
                            ? <><b>Serveur de test — tous les envois {portee} partent à {environnement.redirection_adresse}</b>, avec
                                en tête « Aurait été envoyé à : … ». </>
                            : <><b>Serveur de test — aucune adresse de redirection n'est réglée : aucun message ne part.</b> </>}
                        Réglé par le super-administrateur (Configuration → Redirection des envois).
                        {' '}<span className="hint">En production, ce bandeau n'existe pas.</span>
                    </div>
                </div>
            )}
        </>
    );
}

/** La modale « Envoi des courriels — QLIO ». */
export function CourrielModale({ departement, onFermer, onChange, showMsg }) {
    const [etat, setEtat] = useState(null);
    const [form, setForm] = useState(null);
    const [fournInitial, setFournInitial] = useState(null);
    const [saisieMdp, setSaisieMdp] = useState(false);
    const [resultatTest, setResultatTest] = useState(null);
    const [erreur, setErreur] = useState('');
    const [occupe, setOccupe] = useState(false);

    const appliquer = useCallback((e) => {
        setEtat(e);
        setForm({
            fournisseur: e.fournisseur || 'partage',
            hote: e.hote || '',
            port: e.port ?? '',
            chiffrement: e.chiffrement || '',
            adresse: e.adresse || '',
            identifiant: e.identifiant || '',
            mot_de_passe: '',
            plafond_jour: e.plafond_jour ?? '',
            actif: !!e.actif
        });
        setFournInitial(e.fournisseur || 'partage');
        setSaisieMdp(!e.mot_de_passe_defini);
    }, []);

    useEffect(() => {
        adminAPI.getCourriel(departement.id)
            .then((r) => appliquer(r.data))
            .catch((err) => setErreur(err.response?.data?.error || 'Erreur de lecture des réglages'));
    }, [departement.id, appliquer]);

    if (!form || !etat) {
        return (
            <div className="admin-modal-overlay" onClick={onFermer}>
                <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-modal-b">{erreur || <><i className="fa-solid fa-spinner fa-spin" /> Chargement…</>}</div>
                </div>
            </div>
        );
    }

    const fourn = etat.fournisseurs?.[form.fournisseur] || {};
    const avecAuth = fourn.authentification !== false;
    const change = (cle, valeur) => setForm((f) => ({ ...f, [cle]: valeur }));
    const changerFournisseur = (cle) => {
        const p = etat.fournisseurs?.[cle] || {};
        // Préremplissage (§3 bis). Partage : vide = le réglage commun (affiché en indication).
        setForm((f) => ({
            ...f, fournisseur: cle,
            hote: p.hote || '', port: p.hote === null ? '' : (p.port ?? ''), chiffrement: p.hote === null ? '' : (p.chiffrement || '')
        }));
        if (!p.authentification) setSaisieMdp(false);
    };
    const bascule = form.fournisseur !== fournInitial;
    const echecs = etat.echecs?.nb || 0;
    const activable = etat.test_ok === true && !bascule;
    /**
     * ⚠️ Le champ du mot de passe est AFFICHÉ dès qu'aucun n'est enregistré, quel que soit
     * `saisieMdp` (défaut trouvé au banc image du 2026-10-03 : passer par « Relais » puis
     * revenir à « Partage » remettait `saisieMdp` à faux — le champ restait visible, mais
     * le mot de passe saisi ne partait pas). Une seule règle pour l'affichage ET l'envoi.
     */
    const champMdpVisible = saisieMdp || !etat.mot_de_passe_defini;
    // Ce qui part au serveur : l'activation seulement si elle est possible (sinon refus TEST_REQUIS).
    const corps = () => ({ ...form, actif: form.actif && activable, mot_de_passe: champMdpVisible ? form.mot_de_passe : '' });
    // Le formulaire diffère-t-il de ce qui est enregistré ? (sinon le test n'enregistre rien)
    const modifie = () => !etat.configuree || (champMdpVisible && !!form.mot_de_passe)
        || ['fournisseur', 'hote', 'chiffrement', 'adresse', 'identifiant'].some((k) => String(form[k] ?? '') !== String(etat[k] ?? ''))
        || String(form.port ?? '') !== String(etat.port ?? '')
        || String(form.plafond_jour ?? '') !== String(etat.plafond_jour ?? '');

    const enregistrer = async () => {
        setOccupe(true); setErreur('');
        try {
            const r = await adminAPI.saveCourriel(departement.id, corps());
            appliquer(r.data);
            setResultatTest(null);
            showMsg(r.data.message);
            onChange();
            onFermer();
        } catch (err) {
            setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
        } finally { setOccupe(false); }
    };

    /**
     * ⚠️ Le test porte sur les réglages EN PLACE (enregistrés). Si le formulaire a
     * changé, on enregistre d'abord — sinon on testerait autre chose que ce qu'on voit.
     */
    const tester = async () => {
        setOccupe(true); setErreur(''); setResultatTest(null);
        try {
            if (modifie()) {
                const r0 = await adminAPI.saveCourriel(departement.id, corps());
                appliquer(r0.data);
            }
            const r = await adminAPI.testerCourriel(departement.id);
            appliquer(r.data.etat);
            setResultatTest(r.data);
            onChange();
        } catch (err) {
            setErreur(err.response?.data?.error || 'Erreur pendant le message de test');
        } finally { setOccupe(false); }
    };

    return (
        <div className="admin-modal-overlay" onClick={onFermer}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                <div className="admin-modal-h">
                    <i className="fa-solid fa-envelope" />
                    Envoi des courriels — {departement.code}
                    <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
                </div>

                <div className="admin-modal-b admin-page">
                    <BandeauxEnvironnement environnement={etat.environnement} portee="de ce département" />

                    {echecs > 0 && etat.actif && (
                        <div className="mail-band ko">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div>
                                <b>{echecs} envoi{echecs > 1 ? 's' : ''} en échec depuis le {dateCourte(etat.echecs.depuis)}.</b>{' '}
                                Dernière erreur : « {etat.echecs.dernier} ». Les envois concernés sont signalés sur les dossiers, avec « Renvoyer ».
                            </div>
                        </div>
                    )}

                    <p className="hint" style={{ marginBottom: 12 }}>
                        Les messages partent de cette boîte au nom de la personne qui envoie — « Marie Dupont
                        ({etat.commun?.mention}) » — et les réponses vont chez elle. Les utilisateurs n'ont rien à régler.
                    </p>

                    <div className="fg">
                        <label>Fournisseur</label>
                        <select value={form.fournisseur} onChange={(e) => changerFournisseur(e.target.value)}>
                            {FOURNISSEURS.map((f) => <option key={f.cle} value={f.cle}>{f.libelle}</option>)}
                        </select>
                    </div>

                    {bascule && etat.configuree && (
                        <div className="mail-band warn">
                            <i className="fa-solid fa-power-off" />
                            <div>Changer de fournisseur <b>désactive l'envoi</b> jusqu'à un nouveau message de test réussi. Le changement est noté au journal.</div>
                        </div>
                    )}
                    {form.fournisseur === 'gmail' && (
                        <div className="mail-band warn">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <div>
                                <b>Pour les tests sur le serveur de test seulement.</b> Un message venant d'une adresse Gmail qui
                                demande de cliquer un lien ressemble à de l'hameçonnage, et les données des étudiants passeraient
                                par un compte Google hors de l'université. Exige la validation en deux étapes et un
                                {' '}<b>mot de passe d'application</b> créé dans le compte Google.
                            </div>
                        </div>
                    )}
                    {form.fournisseur === 'relais' && (
                        <div className="mail-band info">
                            <i className="fa-solid fa-circle-info" />
                            <div>
                                Serveur et port donnés par la DSI. Aucun mot de passe : le relais reconnaît l'adresse du serveur.
                                Si le test répond « Relaying denied », la DSI n'a pas encore autorisé le serveur.
                            </div>
                        </div>
                    )}

                    <details className="mail-avance" open={form.fournisseur === 'relais' || form.fournisseur === 'autre'}>
                        <summary>Réglages du serveur</summary>
                        <div className="field-grid" style={{ marginTop: 10 }}>
                            <div className="fg">
                                <label>Serveur</label>
                                <input value={form.hote} placeholder={etat.commun?.hote} onChange={(e) => change('hote', e.target.value)} />
                            </div>
                            <div className="fg">
                                <label>Port</label>
                                <input type="number" value={form.port} placeholder={String(etat.commun?.port || '')} onChange={(e) => change('port', e.target.value)} />
                            </div>
                        </div>
                        <div className="fg">
                            <label>Chiffrement</label>
                            <select value={form.chiffrement} onChange={(e) => change('chiffrement', e.target.value)}>
                                <option value="">Réglage par défaut ({(etat.commun?.chiffrement || '').toUpperCase()})</option>
                                <option value="starttls">STARTTLS</option>
                                <option value="tls">TLS</option>
                                <option value="aucun">Aucun</option>
                            </select>
                            {form.fournisseur === 'partage' && <span className="hint">Vides = le réglage par défaut (Configuration).</span>}
                        </div>
                    </details>

                    <div className="fg">
                        <label>Adresse de la boîte (expéditeur)</label>
                        <input value={form.adresse} placeholder="secretariat…@univ-evry.fr" onChange={(e) => change('adresse', e.target.value)} />
                    </div>

                    {avecAuth && (
                        <div className="fg">
                            <label>Identifiant de connexion</label>
                            <input value={form.identifiant} placeholder={form.adresse} onChange={(e) => change('identifiant', e.target.value)} />
                            <span className="hint">Pour Partage : l'adresse complète (vide = l'adresse de la boîte).</span>
                        </div>
                    )}

                    {avecAuth && (
                        <div className="fg">
                            <label>{form.fournisseur === 'gmail' ? "Mot de passe d'application (16 caractères)" : 'Mot de passe de la boîte'}</label>
                            {!champMdpVisible ? (
                                <div className="mail-secret">
                                    <i className="fa-solid fa-lock" />
                                    <span className="grow">●●●●●●●● <span className="hint">défini le {dateCourte(etat.mot_de_passe_defini_le)}{etat.mot_de_passe_defini_par ? ` par ${etat.mot_de_passe_defini_par}` : ''}</span></span>
                                    <button className="a-btn a-btn-secondary a-btn-sm" onClick={() => setSaisieMdp(true)}>
                                        <i className="fa-solid fa-rotate" /> Remplacer
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input type="password" autoComplete="new-password" value={form.mot_de_passe}
                                           placeholder="Saisi une fois, chiffré aussitôt"
                                           onChange={(e) => change('mot_de_passe', e.target.value)} />
                                    <span className="hint">Chiffré dès réception ; il ne sera <b>plus jamais affiché</b>, même aux administrateurs.</span>
                                    {!etat.environnement?.chiffrement_disponible && (
                                        <span className="hint" style={{ color: '#b91c1c' }}>⚠ Le chiffrement n'est pas configuré sur ce serveur : aucun mot de passe ne peut être enregistré.</span>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="field-grid">
                        <div className="fg">
                            <label>Plafond par jour</label>
                            <input type="number" min={1} max={5000} value={form.plafond_jour}
                                   placeholder={String(etat.commun?.plafond || '')}
                                   onChange={(e) => change('plafond_jour', e.target.value)} />
                            <span className="hint">Au-delà, les envois sont refusés avec un message clair.</span>
                        </div>
                        <div className="fg">
                            <label>Envoi activé</label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                                <span className="switch">
                                    <input type="checkbox" checked={form.actif && activable} disabled={!activable}
                                           onChange={(e) => change('actif', e.target.checked)} />
                                    <span className="sl-tr" />
                                </span>
                                <span className="hint">{activable ? 'Le test a réussi : vous pouvez activer' : 'Possible après un test réussi'}</span>
                            </label>
                        </div>
                    </div>

                    <div className="mail-test">
                        <button className="a-btn a-btn-secondary a-btn-sm" onClick={tester} disabled={occupe}>
                            <i className={`fa-solid ${occupe ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} /> Envoyer un message de test
                        </button>
                        <span className="hint">
                            {etat.teste_le
                                ? <>Dernier test : {dateHeure(etat.teste_le)} — {etat.test_ok
                                    ? <span className="mail-res ok">réussi</span>
                                    : <span className="mail-res ko">échec</span>}</>
                                : 'Aucun test pour l\'instant.'}
                        </span>
                        {resultatTest && (
                            <div className={'mail-res ' + (resultatTest.ok ? 'ok' : 'ko')} style={{ flexBasis: '100%' }}>
                                {resultatTest.ok
                                    ? <><i className="fa-solid fa-circle-check" /> Message de test accepté et envoyé à <b>{resultatTest.destinataire}</b>
                                        {resultatTest.redirige_vers ? ' (adresse de redirection du serveur de test)' : ''}
                                        {resultatTest.serveur_local ? ' — dans Mailpit (http://localhost:8025)' : ''}. Vérifiez l'expéditeur affiché et que « Répondre » vous vise.</>
                                    : <><i className="fa-solid fa-circle-xmark" /> Échec : « {resultatTest.erreur} »</>}
                            </div>
                        )}
                        {!resultatTest && etat.test_ok === false && etat.test_erreur && (
                            <div className="mail-res ko" style={{ flexBasis: '100%' }}><i className="fa-solid fa-circle-xmark" /> « {etat.test_erreur} »</div>
                        )}
                        <span className="hint" style={{ flexBasis: '100%' }}>Si vous avez modifié la connexion, le test l'enregistre d'abord (l'envoi est alors désactivé jusqu'à ce qu'il réussisse), puis l'essaie.</span>
                    </div>

                    {erreur && <div className="mail-band ko" style={{ marginTop: 12, marginBottom: 0 }}><i className="fa-solid fa-circle-xmark" /><div>{erreur}</div></div>}

                    {(etat.historique || []).length > 0 && (
                        <details className="mail-avance" style={{ marginTop: 12, marginBottom: 0 }}>
                            <summary>Historique des réglages</summary>
                            <div style={{ marginTop: 8 }}>
                                {etat.historique.map((h, i) => (
                                    <div key={i} className="hint" style={{ padding: '3px 0', borderBottom: '1px solid #eef2f7' }}>
                                        <b>{dateHeure(h.le)}</b>{h.par ? ` · ${h.par}` : ''} — {h.quoi}
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>

                <div className="admin-modal-f">
                    <button className="a-btn a-btn-secondary" onClick={onFermer}>Annuler</button>
                    <button className="a-btn a-btn-primary" onClick={enregistrer} disabled={occupe}>
                        <i className="fa-solid fa-check" /> Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Configuration (super-administrateur) : le réglage par défaut + la redirection du serveur de test. */
export function CourrielConfiguration({ settings, environnement, onEnregistre, showMsg, showErr }) {
    const [f, setF] = useState(null);
    const [redir, setRedir] = useState('');

    useEffect(() => {
        if (!settings) return;
        setF({
            smtp_hote: settings.smtp_hote || '', smtp_port: settings.smtp_port || '',
            smtp_chiffrement: settings.smtp_chiffrement || 'starttls',
            smtp_plafond_jour_defaut: settings.smtp_plafond_jour_defaut || '',
            smtp_mention_via: settings.smtp_mention_via || ''
        });
        setRedir(settings.smtp_redirection || '');
    }, [settings]);

    if (!f) return null;
    const enregistrer = async (valeurs) => {
        try {
            const r = await adminAPI.saveSettings(valeurs);
            showMsg(r.data.message);
            onEnregistre();
        } catch (err) {
            showErr(err.response?.data?.error || 'Erreur enregistrement');
        }
    };

    return (
        <div className="grid-2">
            <div className="a-card">
                <div className="a-card-h"><i className="fa-solid fa-server" /> Envoi des courriels — réglage par défaut</div>
                <p className="hint" style={{ marginBottom: 10 }}>
                    Le fournisseur proposé aux départements (chacun peut en choisir un autre dans sa carte, onglet Départements).
                </p>
                <div className="field-grid">
                    <div className="fg"><label>Serveur</label><input value={f.smtp_hote} onChange={(e) => setF({ ...f, smtp_hote: e.target.value })} /></div>
                    <div className="fg"><label>Port</label><input type="number" value={f.smtp_port} onChange={(e) => setF({ ...f, smtp_port: e.target.value })} /></div>
                </div>
                <div className="field-grid">
                    <div className="fg">
                        <label>Chiffrement</label>
                        <select value={f.smtp_chiffrement} onChange={(e) => setF({ ...f, smtp_chiffrement: e.target.value })}>
                            <option value="starttls">STARTTLS</option><option value="tls">TLS</option><option value="aucun">Aucun</option>
                        </select>
                    </div>
                    <div className="fg">
                        <label>Plafond par jour (défaut)</label>
                        <input type="number" min={1} max={5000} value={f.smtp_plafond_jour_defaut} onChange={(e) => setF({ ...f, smtp_plafond_jour_defaut: e.target.value })} />
                        <span className="hint">Valeur provisoire : la limite de Partage est à demander à la DSI.</span>
                    </div>
                </div>
                <div className="fg">
                    <label>Mention après le nom de l'expéditeur</label>
                    <input value={f.smtp_mention_via} maxLength={60} onChange={(e) => setF({ ...f, smtp_mention_via: e.target.value })} />
                    <span className="hint">Le destinataire voit « Marie Dupont ({f.smtp_mention_via || '…'}) ».</span>
                </div>
                <button className="a-btn a-btn-primary a-btn-sm" onClick={() => enregistrer(f)}>
                    <i className="fa-solid fa-check" /> Enregistrer
                </button>
            </div>

            {/* ⚠️ N'existe que sur un serveur qui IMPOSE la redirection (dev) — §4 bis */}
            {environnement?.redirection_imposee && (
                <div className="a-card" style={{ borderColor: '#fde68a' }}>
                    <div className="a-card-h"><i className="fa-solid fa-flask" style={{ color: '#b45309' }} /> Redirection des envois — serveur de test</div>
                    <div className="mail-band warn">
                        <i className="fa-solid fa-circle-info" />
                        <div>
                            Ce bloc n'existe que sur un serveur de <b>test</b>. <b>Tous</b> les messages, envois automatiques compris,
                            partent à cette adresse, avec en tête « Aurait été envoyé à : … ». <b>Sans adresse, aucun envoi ne part.</b>
                            {' '}En production, ce bloc n'apparaît pas.
                        </div>
                    </div>
                    <div className="fg">
                        <label>Adresse de redirection</label>
                        <input value={redir} placeholder="votre.adresse@univ-evry.fr" onChange={(e) => setRedir(e.target.value)} />
                    </div>
                    <button className="a-btn a-btn-primary a-btn-sm" onClick={() => enregistrer({ smtp_redirection: redir })}>
                        <i className="fa-solid fa-check" /> Enregistrer
                    </button>
                </div>
            )}
        </div>
    );
}
